import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import LineUserIcon from 'LuxKit/Icons/line/LineUserIcon'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import FormattedOccupancy from 'components/Common/OccupancyPicker/FormattedOccupancy'
import focusStyle from 'styles/utils/focusStyle'
import zIndex from 'styles/tools/z-index'
import TravellerSelectionModal, { type TravellerSelectionModalProps } from './TravellerSelectionModal'

const PickerAnchor = styled.div`
  width: 100%;

  &.elevated {
    position: relative;
    z-index: ${zIndex.menu};
  }
`

const PickerTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: ${rem(8)};
  gap: ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    ${focusStyle}
  }
`

const IconWrap = styled.span`
  display: flex;
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
`

const ChevronWrap = styled.span`
  display: flex;
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
`

export type Props = Omit<
  TravellerSelectionModalProps,
  'isOpen' | 'onClose' | 'anchorRef' | 'triggerRef'
> & {
  className?: string
}

function TravellerSelectionPicker(props: Props) {
  const {
    occupancies,
    onChange,
    onApply,
    className,
    disableSaleUnit,
    saleUnit,
    portaled,
    ...occupancyPickerProps
  } = props
  const intl = useIntl()
  const anchorRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)

  const openSheet = useCallback(() => {
    setIsSheetOpen(true)
  }, [])

  const closeSheet = useCallback(() => {
    setIsSheetOpen(false)
  }, [])

  const guestsLabel = intl.formatMessage({
    id: '0PyVbFYRn2Zo_9k9Oqj-',
    defaultMessage: 'Guests',
    description: 'Label for guests section in search travellers input',
  })

  const selectTravellersLabel = intl.formatMessage({
    id: 'Y3QL1w1uSmK8CuH9qcty',
    defaultMessage: 'Select adults/children',
    description: 'Placeholder text when no guests are selected in search travellers input',
  })

  const hasOccupancies = occupancies.length > 0

  const formattedSaleUnit = useMemo(() => {
    if (disableSaleUnit) {
      return undefined
    }
    return saleUnit
  }, [disableSaleUnit, saleUnit])

  let summaryContent: ReactNode = selectTravellersLabel
  if (hasOccupancies) {
    summaryContent = (
      <FormattedOccupancy
        adultLabel="traveller"
        occupancies={occupancies}
        saleUnit={formattedSaleUnit}
      />
    )
  }

  return (
    <>
      <PickerAnchor
        ref={anchorRef}
        className={clsx(className, isSheetOpen && 'elevated')}
      >
        <PickerTrigger
          ref={triggerRef}
          type="button"
          onClick={openSheet}
          aria-haspopup="dialog"
          aria-expanded={isSheetOpen}
        >
          <IconWrap aria-hidden>
            <LineUserIcon width={24} height={24} />
          </IconWrap>
          <TextBlock>
            <Caption colour="normal-contrast" variant="medium" weight="bold">
              {guestsLabel}
            </Caption>
            <BodyText variant="medium" colour="light-foreground-alt">
              {summaryContent}
            </BodyText>
          </TextBlock>
          <ChevronWrap aria-hidden>
            <DropdownChevron />
          </ChevronWrap>
        </PickerTrigger>
      </PickerAnchor>
      <TravellerSelectionModal
        {...occupancyPickerProps}
        anchorRef={anchorRef}
        triggerRef={triggerRef}
        isOpen={isSheetOpen}
        onClose={closeSheet}
        occupancies={occupancies}
        onChange={onChange}
        onApply={onApply}
        disableSaleUnit={disableSaleUnit}
        saleUnit={saleUnit}
        portaled={portaled}
      />
    </>
  )
}

export default TravellerSelectionPicker
